import { Tooltip, makeStyles } from '@fluentui/react-components';
import { IDENTIFIER } from '../theme/tokens';

const useStyles = makeStyles({
  mono: {
    ...IDENTIFIER,
    fontSize: '12px',
  },
});

interface TenantLabelProps {
  // The admin-entered domain label (FD-110). Preferred display value.
  domain: string | null | undefined;
  // The raw tenant GUID. Shown as a mono tooltip when a domain is present, or
  // as the (shortened) primary value when no domain was captured.
  tenantId: string | null | undefined;
  // Rendered when neither a domain nor a tenant ID is present (e.g. D2C).
  fallback?: string;
}

// Tenant display for the list/detail surfaces (FD-110): the domain is the human
// label; the GUID is demoted into a Fluent Tooltip. Falls back to
// the shortened GUID when no domain was captured (records created before FD-110,
// or GUID-only creates), and to `fallback` when neither is present.
export default function TenantLabel({ domain, tenantId, fallback = '—' }: TenantLabelProps) {
  const styles = useStyles();
  const domainLabel = domain?.trim() || null;
  const id = tenantId?.trim() || null;

  if (domainLabel) {
    // Domain primary; full GUID in a mono tooltip when we have it.
    if (id) {
      return (
        <Tooltip
          relationship="description"
          content={<span className={styles.mono}>{id}</span>}
        >
          <span>{domainLabel}</span>
        </Tooltip>
      );
    }
    return <span>{domainLabel}</span>;
  }

  if (id) {
    // No domain label — show the shortened GUID (mono) with the full value in a tooltip.
    return (
      <Tooltip relationship="description" content={<span className={styles.mono}>{id}</span>}>
        <span className={styles.mono}>{shortenId(id)}</span>
      </Tooltip>
    );
  }

  return <span>{fallback}</span>;
}

function shortenId(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 8)}…`;
}
